import React from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip,
} from 'recharts';
import Card from '../../Card';
import DegradedBanner from '../DegradedBanner';

// dim_config_hardening 修复点 #8:维度名渲染改为多行 + 字体随维度数自适应。
// 老逻辑 slice(0,4) 强切让 "提示词表达" → "提示词表"(丢"达"),
// "结果判断与校验" → "结果判断校"(丢"验"),"风险意识与风险警示" → 残废。
// 现在按 maxCharsPerLine 切成 ≤2 行,字体随维度数缩放。
function formatDimLabel(name, dimCount) {
  let s = (name || '').replace(/^AI\s*/, '');
  s = s.replace(/(能力|意识|度)$/, '').replace(/与/g, '');

  const fontSize = dimCount >= 9 ? 9 : 10;
  const maxCharsPerLine = dimCount >= 9 ? 3 : 4;

  if (s.length <= maxCharsPerLine) return { lines: [s], fontSize };
  // 切两行;> maxCharsPerLine*2 也截断,保持视觉整洁
  return {
    lines: [
      s.slice(0, maxCharsPerLine),
      s.slice(maxCharsPerLine, maxCharsPerLine * 2),
    ].filter(Boolean),
    fontSize,
  };
}

function dimBarColor(score, notTested) {
  if (notTested) return '#94a3b8';
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#2563eb';
  return '#d97706';
}

// recharts PolarAngleAxis 自定义 tick:多行标签 + 自适应字体。
function makePolarTickRenderer(dimCount) {
  return function PolarTick({ payload, x, y, textAnchor }) {
    const { lines, fontSize } = formatDimLabel(payload.value, dimCount);
    return (
      <text x={x} y={y} textAnchor={textAnchor} fill="#64748b" fontSize={fontSize}>
        {lines.map((line, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : fontSize + 2}>{line}</tspan>
        ))}
      </text>
    );
  };
}

/**
 * V4 P2：能力概览 + 雷达图（150~200 字总体概括）。
 */
export default function V4P2Overview({ report }) {
  const dims = report?.core?.dimension_scores || [];
  const overview = report?.narrative_v4?.overview || {};
  const overviewText = overview.text || '';

  // radarData 直接保留原始 dimension_name,由 PolarTick 动态切行 + 缩字体。
  const radarData = dims
    .filter(d => !d.not_tested)
    .map(d => ({ subject: d.dimension_name, value: d.score, fullMark: 100 }));
  const renderTick = makePolarTickRenderer(radarData.length);

  return (
    <div data-testid="report-v4-p2">
      <Card style={{ padding: '24px 28px', marginBottom: 24, borderLeft: '4px solid var(--color-primary, #1a3a6b)' }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--color-text-muted, #64748b)', letterSpacing: 1 }}>
          能力概览总结
        </h3>
        {overview.degraded && <DegradedBanner />}
        <p style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--color-text)', whiteSpace: 'pre-wrap', margin: 0 }}>
          {overviewText || '（暂无内容）'}
        </p>
      </Card>

      <Card style={{ padding: '28px 32px', marginBottom: 24 }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 18 }}>能力画像</h2>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {radarData.length >= 3 && (
            <div data-testid="report-v4-p2-radar" style={{ flex: '1 1 280px', minWidth: 0, maxWidth: 400, width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  data={radarData}
                  outerRadius={radarData.length >= 9 ? '55%' : '60%'}
                  margin={{ top: 20, right: 32, bottom: 20, left: 32 }}
                >
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" tick={renderTick} />
                  <Radar name="得分" dataKey="value" stroke="#1a3a6b" fill="#1a3a6b" fillOpacity={0.25} />
                  <Tooltip formatter={(v) => [`${v} 分`, '得分']} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div data-testid="report-v4-p2-dim-bars" style={{ flex: '1 1 240px', minWidth: 0 }}>
            {dims.map(d => (
              <div key={d.dimension_id} style={{ marginBottom: 16, opacity: d.not_tested ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {d.dimension_name}
                    {d.not_tested && (
                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#94a3b8', background: '#e2e8f0', padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                        未测试
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: d.not_tested ? '#94a3b8' : 'var(--color-primary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {d.not_tested ? '—' : `${d.score} 分`}
                  </span>
                </div>
                <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: d.not_tested ? '0%' : `${d.score ?? 0}%`,
                    background: dimBarColor(d.score, d.not_tested),
                    borderRadius: 3, transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
